import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { API, Pagination, PoolDTO, PoolHierarchyDTO } from 'src/app/services/api/api';
import { UtilService } from 'src/app/services/util.service';
import { ErrorService } from '../../../services/error.service';
import { UserService } from '../../../services/user.service';
import { TagService } from '../../../services/tag.service';
import { PoolHieratchyDTOAndTags } from '../../../models/taggable.model';
import { DiscussionService } from '../../../services/discussion.service';
import { SbSplitButton } from '../../../services/global/global-interfaces';
import { PermissionService } from '../../../services/permission.service';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { HierarchyService } from '../../../services/transformation-helper/hierarchy.service';
import { isNumberEven } from '../../../services/global/global-methods';
import { BehaviorSubject, Subscription, switchMap } from 'rxjs';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { RoutingID } from '../../../routes/sb-routing-enum';
import { FilterPanelConfig } from '@ngx-seatback/layout';
import { RequestClass } from '../../../services/global/RequestClass';

type PoolHierarchyDTOAndTagsAndButtons = PoolHieratchyDTOAndTags & SbSplitButton<string>;

@Component({
  selector: 'sb-page-pools',
  templateUrl: './pools.component.html',
  styleUrls: ['./pools.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PoolsComponent extends RequestClass {
  public readonly util = inject(UtilService);
  public readonly errorHandler = inject(ErrorService);
  public readonly userStatus = inject(UserService);
  public readonly tagService = inject(TagService);
  public readonly discussionService = inject(DiscussionService);
  public readonly permissionService = inject(PermissionService);
  private readonly api = inject(API);
  private readonly stringTransformation = inject(StringTransformationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly hierarchyService = inject(HierarchyService);

  // table wrapper variables
  hierarchies: PoolHierarchyDTOAndTagsAndButtons[] = [];
  showChildren: Map<number, boolean> = new Map();
  poolDragged: number = undefined;
  dragToRoot: boolean = false;

  // filter variables
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    hideTagSection: true,
    button: { visibleWithoutChanges: true },
  };

  protected readonly isNumberEven = isNumberEven;

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .ss_nm_Pool_retrieveHierarchy(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        },
        this.pagination
      )
      .pipe(
        take(1),
        switchMap(pools => this.tagService.getAndAddTagEntitiesObs(this.tagService.TaggableDTO.Pool, pools)),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
          this.dragToRoot = false;
        })
      )
      .subscribe({
        next: (e: PoolHierarchyDTO[]) => {
          this.hierarchies = this.buildSplitButtons<PoolHieratchyDTOAndTags>(e);
        },
        error: error => {
          this.errorHandler.errorHandler(error);
          this.hierarchies = [];
        },
      });
    this.subs.push(sub);
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<string>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<string>[] };
    });

    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.device_list])) {
        item.sbButtons.push({
          icon: 'playerOnline',
          tooltip: 'lang.globals.devices',
          link: RoutingID.devices,
          pathParams: null,
          queryParams: {
            poolIds: JSON.stringify(item?.id ? [item.id] : []),
          },
        });
      }
      if (this.permissionService.hasPermissions([this.permissionService.Permission.pool_edit])) {
        item.sbButtons.push({
          icon: 'edit',
          tooltip: 'lang.globals.edit',
          link: RoutingID.poolEdit,
          pathParams: [{ id: 'poolID', value: item?.id }],
        });
      }
    });
    return result;
  }

  /*
    // add implementation when other action is needed and add to splitbutton in dom
  resolveSplitButtonAction(campaign: PoolHieratchyDTOAndTagsAndButtons, $event: string) {
    return;
  }
*
  /*
  TODO change when Pool is changed to precondition SBA-538
  private updatePoolHierarchy(currentPool: PoolHierarchyDTO): void {
    this.loading = true;
    this.api
        .ss_nm_Pool_retrieveHierarchy(
            {
              precondition: currentPool.id,
            },
            this.options.pagination
        )
        .pipe(
            takeUntilDestroyed(this.destroyRef),
            take(1),
            finalize(() => {
              this.loading = false;
              this.dragToRoot = false;
            })
        )
        .subscribe({
          next: (e: PoolHierarchyDTO[]) => {
            currentPool.children = e;
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
  }
   */

  toggleShowChildren(pool: PoolHieratchyDTOAndTags) {
    if (this.showChildren.has(pool.id)) {
      this.showChildren.delete(pool.id);
    } else {
      this.showChildren.set(pool.id, true);
      pool.children = this.buildSplitButtons(pool.children);
      // TODO when Pool is changed to precondition SBA-538
      // this.updatePoolHierarchy(pool);
    }
  }

  onmousedown($event: any) {
    let target = $event.target;
    while (target.nodeName !== 'TR') {
      target = target.parentNode;
    }
    target.setAttribute('draggable', 'true');
  }

  onmouseup($event: any) {
    let target = $event.target;
    while (target.nodeName !== 'TR') {
      target = target.parentNode;
    }
    target.setAttribute('draggable', 'false');
  }

  onDragStart($event: any) {
    $event.dataTransfer.setData('poolid', $event.target.attributes['data-pool'].value);
    this.poolDragged = +$event.target.attributes['data-pool'].value;

    const draggedPoolObj: PoolHieratchyDTOAndTags = this.hierarchyService.getObjectFromHierarchyArrayById(
      this.hierarchies,
      { id: this.poolDragged },
      'children'
    );

    if (draggedPoolObj.parent) {
      this.dragToRoot = true;
    }
  }

  onDragEnd($event: any) {
    this.dragToRoot = false;
    let target = $event.target;
    while (target.nodeName !== 'TR') {
      target = target.parentNode;
    }
    target.setAttribute('draggable', 'false');
  }

  onDragOver($event: any) {
    let target = $event.currentTarget;
    while (target.nodeName !== 'TR') {
      target = target.parentNode;
    }
    const draggedPool: number = +this.poolDragged;
    const targetPool: number = target.attributes['data-pool'] ? +target.attributes['data-pool'].value : null;

    if (draggedPool === 0) {
      return;
    }

    if (draggedPool === targetPool) {
      return;
    }

    const draggedPoolObj: PoolHieratchyDTOAndTags = this.hierarchyService.getObjectFromHierarchyArrayById(
      this.hierarchies,
      { id: draggedPool },
      'children'
    );
    const targetPoolObj: PoolHieratchyDTOAndTags = this.hierarchyService.getObjectFromHierarchyArrayById(
      this.hierarchies,
      { id: targetPool },
      'children'
    );

    if (!this.allowSubDrop(draggedPoolObj, targetPoolObj)) {
      return;
    }

    $event.preventDefault();
  }

  allowSubDrop(draggedPoolObj: PoolHieratchyDTOAndTags, targetPoolObj: PoolHieratchyDTOAndTags): boolean {
    if (!targetPoolObj) {
      return true;
    }

    let currentPool: PoolDTO = targetPoolObj;

    while (currentPool) {
      if (currentPool.id === draggedPoolObj.id) {
        return false;
      }
      currentPool = this.hierarchyService.getObjectFromHierarchyArrayById(this.hierarchies, currentPool.parent, 'children');
    }
    return true;
  }

  onDrop($event: any) {
    let target = $event.currentTarget;
    while (target.nodeName !== 'TR') {
      target = target.parentNode;
    }
    const draggedPool: number = +this.poolDragged;
    const targetPool: number = target.attributes['data-pool'] ? +target.attributes['data-pool'].value : null;

    const draggedPoolObj: PoolHieratchyDTOAndTags = this.hierarchyService.getObjectFromHierarchyArrayById(
      this.hierarchies,
      { id: draggedPool },
      'children'
    );
    const targetPoolObj: PoolHieratchyDTOAndTags = this.hierarchyService.getObjectFromHierarchyArrayById(
      this.hierarchies,
      { id: targetPool },
      'children'
    );

    this.hierarchyService.removeObjectFromHierarchyArrayById(this.hierarchies, draggedPoolObj, 'children');

    if (targetPoolObj) {
      targetPoolObj.children.push(draggedPoolObj);
    } else {
      this.hierarchies.push(draggedPoolObj);
    }

    this.refreshParents();

    this.refreshPoolDragged(draggedPoolObj);
  }

  refreshPoolDragged(draggedPoolObj: PoolHieratchyDTOAndTags) {
    const tempPool: PoolHieratchyDTOAndTags = { ...draggedPoolObj };
    tempPool.children = [];

    if (tempPool.parent) {
      (tempPool.parent as PoolHieratchyDTOAndTags).children = [];
      tempPool.parent.parent = null;
    }

    this.api
      .ss_nm_Pool_edit(tempPool)
      .pipe(take(1))
      .subscribe(() => {
        this.requestDebounced();
      });
  }

  refreshParents() {
    for (const pool of this.hierarchies) {
      this.refreshParent(pool, null);
    }
  }

  refreshParent(pool: PoolHieratchyDTOAndTags, parent?: PoolHieratchyDTOAndTags) {
    pool.parent = parent;
    for (const child of pool.children) {
      this.refreshParent(child, pool);
    }
  }

  getPoolId(pool: PoolHierarchyDTOAndTagsAndButtons) {
    const arr: number[] = [];
    if (pool) {
      arr.push(pool.id);
    }
    return JSON.stringify(arr);
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
  }
}
